import * as React from 'react'
import { connect } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { State } from 'store'
import ActionButton from 'uiComponents/buttons'
import UserListTable from './userListTable'
import { SectionForm, Section } from 'uiComponents/settingsContainer'
import { TableLoader } from 'uiComponents/loaders'
import { UserListItem, UserRole } from 'settings/schema'
import SearchField, { SearchContainer } from 'uiComponents/search/searchField'
import { BaseRouteParams, useNavigation } from 'hocs'
import { AppServices } from 'middleware'
import { AnyAction, bindActionCreators, Dispatch } from 'redux'
import { replaceMessage } from 'uiComponents/messages/actions'
import { ChooseUserRoleModal } from './chooseUserRoleModal'
import { useHasFeature } from 'features'
import { useAdminPermission } from 'admin/hocs'
import { useTranslation } from 'translations'

interface UserListSectionProps extends ReturnType<typeof mapStateToProps>, ReturnType<typeof mapDispatchToProps> {}

function UserListSection({ accounts, ownEmail, replaceMessages }: UserListSectionProps) {
    const { t } = useTranslation()
    const [userList, setUserList] = React.useState<UserListItem[]>([])
    const [loading, setLoading] = React.useState<boolean>(false)
    const { accountSlug } = useParams<BaseRouteParams>()
    const navigation = useNavigation()
    const history = useHistory()
    const hasResellersFeature = useHasFeature('ResellersFeature')
    const isConviousAdmin = useAdminPermission().isAdmin

    async function getData() {
        setLoading(true)
        try {
            const query = navigation.query()
            const data = await AppServices.accountSettingsService.getUserList(accountSlug, query.q, query.filter)
            setUserList(data)
        } catch {
            replaceMessages('unknown_error', 'error', t('settings.users.list.loadError'))
        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => {
        getData()
    }, [accountSlug, navigation.query().q, navigation.query().filter])

    const activeAccount = accounts.find((x) => x.slug === accountSlug) || accounts[0]
    const nonStaffUsers = userList.length > 0 ? userList.filter((u) => u.role !== 'staff') : []

    const [showAddNewUserModal, setShowAddNewUserModal] = React.useState(false)

    const userOptions: UserRole[] = ['user', 'admin']
    if (hasResellersFeature) {
        userOptions.push('reseller')
    }
    if (isConviousAdmin) {
        userOptions.push('staff')
    }

    const handleSelectUserRole = (userRole: UserRole) => {
        setShowAddNewUserModal(false)
        history.push(`/account/${accountSlug}/settings/users/user_form/create?role=${userRole}`)
    }

    const handleAddNewUser = () => {
        if (userOptions.length > 1) {
            setShowAddNewUserModal(true)
        } else {
            handleSelectUserRole(userOptions[0])
        }
    }

    return (
        <>
            <Section>
                <SectionForm onSubmit={(e) => e.preventDefault()}>
                    {loading && <TableLoader />}
                    {!loading && (
                        <>
                            <SearchContainer style={{ margin: '1.5em' }}>
                                <SearchField
                                    placeholder={t('settings.users.list.searchPlaceholder')}
                                    finishTypingDelay={1500}
                                />
                                <ActionButton kind="action" size="medium" id="add-user" onClick={handleAddNewUser}>
                                    {t('settings.users.list.addNew')}
                                </ActionButton>
                            </SearchContainer>
                            {nonStaffUsers.length === 0 && (
                                <div style={{ marginBottom: '5em', marginLeft: '1.5em' }} id="no-users-message">
                                    {t('settings.users.list.emptyState', { name: activeAccount.name })}
                                </div>
                            )}
                            {nonStaffUsers.length > 0 && (
                                <UserListTable
                                    users={nonStaffUsers}
                                    ownEmail={ownEmail}
                                    accountSlug={accountSlug}
                                    history={history}
                                    replaceMessages={replaceMessages}
                                />
                            )}
                        </>
                    )}
                </SectionForm>
            </Section>
            {showAddNewUserModal && (
                <ChooseUserRoleModal
                    options={userOptions}
                    onSelectUserRole={handleSelectUserRole}
                    onDismiss={() => setShowAddNewUserModal(false)}
                />
            )}
        </>
    )
}

const mapStateToProps = (state: State) => {
    return {
        ownEmail: state.auth.user ? state.auth.user.username : '',
        accounts: state.auth.user ? state.auth.user.accounts : [],
    }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => {
    return bindActionCreators(
        {
            replaceMessages: replaceMessage,
        },
        dispatch,
    )
}
export default connect(mapStateToProps, mapDispatchToProps)(UserListSection)
